import Modal from "@/Components/Modal";
import CloseIcon from "@/Components/Icons/CloseIcon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCheckCircle,
    faDownload,
    faEnvelope,
    faExclamationCircle,
    faFileVideo,
    faInfoCircle,
    faLanguage,
    faSpinner,
    faUpRightFromSquare
} from "@fortawesome/free-solid-svg-icons";
import React, {useEffect} from "react";
import {EmailNotificationStep, TranslationStep, WorkflowExecution} from "@/types/workflow";
import moment from "moment/moment";
import {getWorkflowExecution} from "@/services/workflowService";
import toast from "react-hot-toast";
import {getLanguageName} from "@/utils/textFormatting";
import {ExtractionJobStatus} from "@/types/enums";

export interface WorkflowExecutionModalProps {
    show: boolean;
    onClose: () => void;
    executionId: number | null;
}

interface JobFailure {
    filename: string;
    error: string;
}

interface TranslationFailure {
    filename: string;
    language: string;
    error: string;
}

const getTranslationFailures = (execution: WorkflowExecution): TranslationFailure[] => {
    const translationContext = execution.context?.translation;
    if (!translationContext) return [];

    const failures: TranslationFailure[] = [];

    if (translationContext.failedSubmissions) {
        translationContext.failedSubmissions.forEach((failure: any) => {
            const extractionJob = execution.extraction_jobs.find(job => job.id === failure.extraction_job_id);
            failures.push({
                filename: extractionJob?.file_name || 'Unknown file',
                language: getLanguageName(failure.language),
                error: failure.error
            });
        });
    }

    if (execution.translation_jobs.length) {
        execution.translation_jobs.forEach(job => {
            if (job.status !== 'completed' && job.status !== 'running') {
                const extractionJob = execution.extraction_jobs
                    .find(j => j.id === job.extraction_job_id);
                failures.push({
                    filename: extractionJob?.file_name || 'Unknown file',
                    language: getLanguageName(job.language),
                    error: 'Translation failed'
                });
            }
        });
    }

    return failures;
};

const getJobErrorMessage = (status: ExtractionJobStatus): string => {
    switch (status) {
        case ExtractionJobStatus.Failed:
            return 'Processing failed';
        case ExtractionJobStatus.Cancelled:
            return 'Processing was cancelled';
        default:
            return 'No subtitles detected';
    }
};

const getTranslationSummary = (execution: WorkflowExecution) => {
    const translationContext = execution.context?.translation;
    if (!translationContext) return null;

    const submittedJobs = execution.translation_jobs;
    const failedSubmissions = translationContext.failedSubmissions || [];

    const totalTranslations = execution.translation_jobs.length + failedSubmissions.length;
    const successfulTranslations = submittedJobs
        .filter(job => job.status === 'completed')
        .length;
    const failedTranslations = totalTranslations - successfulTranslations;

    return {
        total: totalTranslations,
        successful: successfulTranslations,
        failed: failedTranslations
    };
};

export default function WorkflowExecutionModal({
    show,
    onClose,
    executionId
}: WorkflowExecutionModalProps) {
    const [execution, setExecution] = React.useState<WorkflowExecution | null>(null);

    useEffect(() => {
        if (executionId) {
            loadExecution(executionId);
        }
    }, [executionId]);

    useEffect(() => {
        if(executionId && execution?.status === 'running') {
            const interval = setInterval(() => loadExecution(executionId), 4_500);
            return () => clearInterval(interval);
        }
    }, [executionId, execution]);

    useEffect(() => {
        if (!show) {
            const timeout = setTimeout(() => setExecution(null), 300)
            return () => clearTimeout(timeout);
        }
    }, [show]);

    const loadExecution = (executionId: number) => {
        getWorkflowExecution(executionId)
            .then(setExecution)
            .catch(err => toast.error(`Failed to load execution details: ${err.message}`));
    }

    const renderStepDetails = (execution: WorkflowExecution) => {
        const executionSteps = Array.isArray(execution.steps) ? execution.steps : [];

        if (executionSteps.length === 0) {
            return (
                <div className="text-gray-500 dark:text-gray-400 text-center py-4">
                    No steps configured
                </div>
            );
        }

        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                {executionSteps.map((step, index, arr) => {
                    const isCurrentStep = index === execution.current_step_index;
                    const isLastStep = index === arr.length - 1;
                    const isCompleted = index < execution.current_step_index || isLastStep && execution.status === 'completed';

                    let stepStatus = 'pending';
                    if (isCompleted) stepStatus = 'completed';
                    else if (isCurrentStep && execution.status === 'running') stepStatus = 'running';
                    else if (isCurrentStep && execution.status === 'failed') stepStatus = 'failed';

                    return (
                        <div
                            key={index}
                            className={`p-4  transition-colors ${
                                !isLastStep ? 'border-b border-gray-200 dark:border-gray-700' : ''
                            }`}
                        >
                            <div className="flex justify-between items-center">
                                <div className="flex items-start space-x-3">
                                    <div
                                        className="mt-0.5 p-2 rounded-full bg-gray-100 dark:bg-gray-700 w-9 h-9 flex items-center justify-center text-gray-400 dark:text-gray-500">
                                        <FontAwesomeIcon
                                            icon={step.type === 'translation' ? faLanguage : faEnvelope}
                                            className="w-4 h-4 text-gray-600 dark:text-gray-400"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                                {step.name}
                                            </h4>

                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                            {step.type === 'translation' ? (
                                                <div>
                                                    <span className="font-medium">Target Languages: </span>
                                                    {
                                                        (step as TranslationStep).config.target_languages
                                                            .map(getLanguageName)
                                                            .join(', ')
                                                    }
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="font-medium">Recipients: </span>
                                                    {(step as EmailNotificationStep).config.recipients.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 flex-shrink-0">
                                    {stepStatus === 'failed' && (
                                        <FontAwesomeIcon
                                            icon={faExclamationCircle}
                                            className="w-4 h-4 text-red-500"
                                        />
                                    )}
                                    {stepStatus === 'running' && (
                                        <FontAwesomeIcon
                                            icon={faSpinner}
                                            className="w-4 h-4 text-blue-500 animate-spin"
                                        />
                                    )}
                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                        stepStatus === 'completed' ? 'bg-green-100 text-green-700' :
                                            stepStatus === 'running' ? 'bg-blue-100 text-blue-700' :
                                                stepStatus === 'failed' ? 'bg-red-100 text-red-700' :
                                                    'bg-gray-100 text-gray-600'
                                    }`}>
                                        {stepStatus === 'completed' ? 'Completed' :
                                            stepStatus === 'running' ? 'Running' :
                                                stepStatus === 'failed' ? 'Failed' :
                                                    'Pending'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return 'Not started';
        return moment(dateString).format('MMM DD, YYYY h:mm:ss A');
    };

    const translationSummary = execution ? getTranslationSummary(execution) : null;

    return (
        <Modal maxWidth="xl" show={show} onClose={onClose} blurBackground={true}>
            {
                execution && (
                    <div className="w-full pb-3">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 py-4 ">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    Workflow Execution Details
                                </h3>

                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-gray-400 dark:text-gray-500 !outline-none bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                            >
                                <CloseIcon/>
                                <span className="sr-only">Close modal</span>
                            </button>
                        </div>


                        <div className="p-6 space-y-6 pt-2">
                            {/* Status Alert */}
                            <div className={`rounded-lg p-4 text-sm ${
                                execution.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20 border border-green-500 dark:border-green-700' :
                                    execution.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700' :
                                        execution.status === 'running' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' :
                                            'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700'
                            }`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        {execution.status === 'completed' && (
                                            <FontAwesomeIcon icon={faCheckCircle} className="w-5 h-5 text-green-600"/>
                                        )}
                                        {execution.status === 'failed' && (
                                            <FontAwesomeIcon icon={faExclamationCircle} className="w-5 h-5 text-red-600"/>
                                        )}
                                        {execution.status === 'running' && (
                                            <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 text-blue-600 animate-spin"/>
                                        )}
                                        <div className={`font-medium ${
                                            execution.status === 'completed' ? 'text-green-800 dark:text-green-200' :
                                                execution.status === 'failed' ? 'text-red-800 dark:text-red-200' :
                                                    execution.status === 'running' ? 'text-blue-800 dark:text-blue-200' :
                                                        'text-gray-800 dark:text-gray-200'
                                        }`}>
                                            {execution.status === 'completed' && execution.completed_at
                                                ? `Completed at ${moment(execution.completed_at).format('MMM D, YYYY h:mm A')}`
                                                : execution.status === 'failed'
                                                    ? ('Failed' + `${execution.error_message ? `: ${execution.error_message}` : ''}`)
                                                    : execution.status === 'running'
                                                        ? 'Running'
                                                        : execution.status.charAt(0).toUpperCase() + execution.status.slice(1)
                                            }
                                        </div>
                                    </div>
                                    {execution.status === 'completed' && execution.download_url && (
                                        <a
                                            href={execution.download_url}
                                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 shadow bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                                           <span> <span>Download</span><span className="hidden md:inline"> Results</span></span>
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 md:space-y-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-5">
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Workflow</div>
                                    <div className="text-gray-800 dark:text-gray-300 text-sm">{execution.workflow.name}</div>
                                </div>

                                <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Started</div>
                                    <div
                                        className="text-gray-800 dark:text-gray-300 text-sm">{formatDateTime(execution.started_at)}</div>
                                </div>
                            </div>


                            {/* Extraction Jobs Section */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                    Extraction Jobs ({execution.extraction_jobs.length})
                                </h4>
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                                    {execution.extraction_jobs.map((job, index) => {
                                        const isSuccess = job.status === ExtractionJobStatus.Succeeded && job.subtitle_count > 0;
                                        const errorMessage = !isSuccess ? getJobErrorMessage(job.status) : null;

                                        return (
                                            <div
                                                key={index}
                                                className={`${
                                                    index < execution.extraction_jobs.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center min-w-0 flex-1">
                                                        <a href={isSuccess ? route('subtitle.edit', {id: job.id}) : '#'}
                                                           className={`flex-1 min-w-0 flex justify-between items-center  p-4 ${isSuccess ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : 'cursor-default'} transition-colors`}
                                                           target={isSuccess ? '_blank' : undefined}>
                                                            <span>
                                                                <FontAwesomeIcon
                                                                    icon={faFileVideo}
                                                                    className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0"
                                                                />
                                                                <span className="text-sm text-gray-900 dark:text-gray-100 truncate"
                                                                      title={job.file_name}>
                                                                {job.file_name}
                                                            </span>
                                                            </span>

                                                            {
                                                                isSuccess ? (
                                                                    <FontAwesomeIcon
                                                                        icon={faUpRightFromSquare}
                                                                        className="w-3 h-3 text-gray-400 dark:text-gray-500"
                                                                    />
                                                                ) : errorMessage && (
                                                                    <div
                                                                        className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1  rounded-full block">
                                                                        {errorMessage}
                                                                    </div>
                                                                )
                                                            }
                                                        </a>
                                                    </div>

                                                </div>

                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {translationSummary && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                        Translation Results
                                    </h4>

                                    {(() => {
                                        const translationFailures = getTranslationFailures(execution);
                                        if (translationFailures.length === 0 && translationSummary.successful > 0) {
                                            return (
                                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
                                                    <div className="flex items-center gap-3">
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0"
                                                        />
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                All translations completed successfully
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                {translationSummary.successful} translation{translationSummary.successful !== 1 ? 's' : ''} completed
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (translationFailures.length > 0) {
                                            return (
                                                <div className="space-y-3">
                                                    {translationSummary?.successful > 0 && (
                                                        <div className="border border-green-200 dark:border-green-700 rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
                                                            <div className="flex items-center gap-3">
                                                                <FontAwesomeIcon
                                                                    icon={faCheckCircle}
                                                                    className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0"
                                                                />
                                                                <div>
                                                                    <div className="text-sm font-medium text-green-800 dark:text-green-200">
                                                                        {translationSummary.successful} translation{translationSummary.successful !== 1 ? 's' : ''} succeeded
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div
                                                        className="border border-red-200 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                                                        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-700 p-4">
                                                            <div className="flex items-center gap-3">
                                                                <FontAwesomeIcon
                                                                    icon={faExclamationCircle}
                                                                    className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0"
                                                                />
                                                                <div>
                                                                    <div className="text-sm font-medium text-red-800 dark:text-red-200">
                                                                        Translation Failures ({translationFailures.length})
                                                                    </div>
                                                                    <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                                                        The following translations could not be completed
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>


                                                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                            {translationFailures.map((failure, index) => (
                                                                <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div
                                                                                className="flex items-center justify-between mb-1">
                                                                                <div
                                                                                    className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                                                                                    title={failure.filename}>
                                                                                    {failure.filename}
                                                                                </div>
                                                                                <span
                                                                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ml-2 flex-shrink-0">
                                                             {failure.language}
                                                         </span>
                                                                            </div>
                                                                            <div className="text-sm text-red-600 dark:text-red-400">
                                                                                {failure.error}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
                                                <div className="flex items-center gap-3">
                                                    <FontAwesomeIcon
                                                        icon={faInfoCircle}
                                                        className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0"
                                                    />
                                                    <div>
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            No translation results available
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Workflow Steps */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                    Workflow Steps
                                </h4>
                                {renderStepDetails(execution)}
                            </div>
                        </div>


                    </div>
                )
            }
        </Modal>
    );
}
